import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { getArticleBySlug } from "@/mocks/articles";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-sm ${i < rating ? "ri-star-fill text-amber-400" : "ri-star-fill text-foreground-200"}`}
        ></i>
      ))}
    </div>
  );
}

const siteUrl = "https://pandabuy.watch";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main>
        <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-database-2-line text-white text-sm"></i>
              </span>
              <span className="font-heading text-lg font-bold text-foreground-950 tracking-tight whitespace-nowrap">
                pandabuy<span className="text-primary-500">.watch</span>
              </span>
            </Link>
            <Link to="/" className="px-5 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
              Back to Home
            </Link>
          </div>
        </nav>
        <div className="py-24 text-center">
          <i className="ri-file-search-line text-6xl text-foreground-300 mb-4 block"></i>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Article Not Found</h1>
          <p className="text-sm text-foreground-500 mt-2">The article you are looking for does not exist.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.description,
            image: article.image,
            datePublished: "2026-06-28",
            dateModified: "2026-07-01",
            author: {
              "@type": "Organization",
              name: "PandabuySpreadsheet",
              url: siteUrl,
            },
            publisher: {
              "@type": "Organization",
              name: "PandabuySpreadsheet",
              url: siteUrl,
              logo: {
                "@type": "ImageObject",
                url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${siteUrl}/article/${article.slug}`,
            },
          }),
        }}
      />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-database-2-line text-white text-sm"></i>
            </span>
            <span className="font-heading text-lg font-bold text-foreground-950 tracking-tight whitespace-nowrap">
              pandabuy<span className="text-primary-500">.watch</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:flex px-4 py-2 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-100/50 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </nav>

      <article className="py-8 md:py-14 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors font-medium flex items-center gap-1">
              <i className="ri-arrow-left-line"></i>
              <span>Home</span>
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700">Article</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <aside className="lg:w-[280px] lg:flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-2xl overflow-hidden mb-5 shadow-md">
                  <img
                    src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20an%20open%20laptop%20showing%20a%20spreadsheet%20interface%20surrounded%20by%20scattered%20product%20photos%20and%20sticky%20notes%20on%20a%20clean%20white%20desk%20modern%20workspace%20productivity%20aesthetic%20bright%20natural%20lighting%20editorial%20lifestyle%20photography&width=600&height=800&seq=article-sidebar-v2&orientation=portrait"
                    alt="Pandabuy spreadsheet guide"
                    className="w-full h-56 object-cover"
                  />
                </div>

                <div className="bg-background-100 rounded-2xl p-5 border border-background-200/60">
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wide">Table of Contents</span>
                  <ul className="mt-3 space-y-2">
                    {article.sections.map((section, i) => (
                      <li key={i}>
                        <a
                          href={`#section-${i}`}
                          className="text-xs text-foreground-600 hover:text-primary-500 transition-colors leading-snug block"
                        >
                          {section.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 bg-primary-50/70 rounded-2xl p-5 border border-primary-200/50">
                  <span className="text-xs font-semibold text-primary-800 uppercase tracking-wide">Related Articles</span>
                  <ul className="mt-3 space-y-2">
                    <li>
                      <Link to="/" className="text-xs text-foreground-600 hover:text-primary-500 transition-colors leading-snug block">
                        <i className="ri-home-line mr-1"></i>Browse our Pandabuy Spreadsheet hub
                      </Link>
                    </li>
                    <li>
                      <Link to="/article/pandabuy-shoes-spreadsheet-guide" className="text-xs text-foreground-600 hover:text-primary-500 transition-colors leading-snug block">
                        <i className="ri-arrow-right-line mr-1"></i>Pandabuy Shoes Spreadsheet Guide
                      </Link>
                    </li>
                    <li>
                      <Link to="/article/pandabuy-budget-shopping-guide" className="text-xs text-foreground-600 hover:text-primary-500 transition-colors leading-snug block">
                        <i className="ri-arrow-right-line mr-1"></i>Budget Shopping Guide Under $200
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <header className="mb-10">
                <div className="inline-block px-3 py-1.5 rounded-lg bg-accent-100/70 text-accent-800 text-xs font-semibold mb-4">
                  {article.tag}
                </div>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.12] text-balance">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 mt-5 text-xs text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {article.readTime} read
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views} views
                  </span>
                </div>
              </header>

              <div className="rounded-2xl overflow-hidden mb-10 shadow-lg">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-52 md:h-72 object-cover"
                />
              </div>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10">
                {article.intro}
              </p>

              {article.sections.map((section, i) => (
                <section key={i} id={`section-${i}`} className="mb-9">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                    {section.heading}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                  {i === 2 && (
                    <div className="my-6 p-5 rounded-2xl bg-accent-50/50 border border-accent-200/50">
                      <p className="text-xs text-accent-800 leading-relaxed">
                        <strong className="text-accent-900">Pro tip:</strong> When browsing the{" "}
                        <Link to="/" className="text-primary-600 font-medium hover:underline">
                          pandabuy spreadsheet hub
                        </Link>
                        , start with the shoes and hoodies categories first — they tend to have the highest community-rated finds and are updated most frequently with fresh discoveries.
                      </p>
                    </div>
                  )}
                  {i === 3 && (
                    <div className="my-6 p-5 rounded-2xl bg-secondary-50/80 border border-secondary-200/50">
                      <p className="text-xs text-secondary-800 leading-relaxed">
                        <strong className="text-secondary-900">Daily reminder:</strong> Our{" "}
                        <Link to="/" className="text-primary-600 font-medium hover:underline">
                          pandabuy spreadsheet homepage
                        </Link>{" "}
                        is refreshed with new finds across all 14 categories every single day. Make it part of your morning routine to catch trending items before they sell out.
                      </p>
                    </div>
                  )}
                </section>
              ))}

              <section className="mb-10 pt-6 border-t border-background-200/60">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">Final Thoughts</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.conclusion}
                </p>
              </section>

              <div className="mt-10 pt-6 border-t border-background-200/60 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to All Finds</span>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>Explore Pandabuy Sheet</span>
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      <footer className="px-4 md:px-6 pb-6 pt-4">
        <div className="max-w-7xl mx-auto bg-foreground-950 rounded-[28px] px-6 md:px-12 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-database-2-line text-white text-sm"></i>
              </span>
              <span className="font-heading text-lg font-bold text-white tracking-tight">
                pandabuy<span className="text-primary-400">.watch</span>
              </span>
            </div>
            <p className="text-[11px] text-foreground-500 text-center">
              This platform provides informational spreadsheets and curated product discovery content only. &copy; 2026 Pandabuy.watch
            </p>
            <Link to="/" className="text-foreground-400 text-xs hover:text-white transition-colors flex items-center gap-1">
              <i className="ri-home-line"></i>
              Home
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}